/**
 * FileCard — compact preview tile for a single chat attachment.
 *
 * Three placements share the same component:
 *
 *   1. Home page composer       (default variant: 168 × 64 tile)
 *   2. Active-chat composer     (default variant: same)
 *   3. Historical user message  (compact variant: ~140 × 40, no remove, no status text)
 *
 * Click anywhere on the card opens AttachmentDetailsDialog which shows the
 * schema, sample rows, full error trace, etc — same modal regardless of
 * placement.  The visible status indicator (spinner / check / warning /
 * error icon) replaces the kind icon while non-idle.
 */
import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

import AttachmentDetailsDialog from './AttachmentDetailsDialog';
import type { DraftAttachment, ServerAttachment } from './types';

export interface FileCardProps {
  draft?: DraftAttachment;
  server?: ServerAttachment;
  /** Called when the user dismisses (× button).  Omit to hide the dismiss control. */
  onRemove?: () => void;
  /** Called when user clicks "Use this parse" on a low-confidence file. */
  onConfirm?: () => void;
  /**
   * `compact` is the small variant used on historical user messages
   * (no remove button, no status text — just the icon + filename).
   */
  variant?: 'default' | 'compact';
}

export default function FileCard({
  draft,
  server,
  onRemove,
  onConfirm,
  variant = 'default',
}: FileCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const filename = server?.filename || draft?.file.name || 'file';
  const kind = server?.kind || (draft?.file ? guessFileKind(draft.file) : 'document');
  const ext = filename.includes('.')
    ? filename.slice(filename.lastIndexOf('.') + 1).toUpperCase()
    : '';

  const isUploading = draft && (draft.uploadState === 'queued' || draft.uploadState === 'uploading');
  const isProcessing = server && (server.status === 'pending' || server.status === 'processing');
  const isFailed = (draft && draft.uploadState === 'error') || server?.status === 'failed';
  const isLowConfidence = server?.parse_confidence === 'low' && !server?.parse_confirmed_at;
  const isRemoved = Boolean(server?.deleted_at);
  const isReady = server?.status === 'ready' && !isLowConfidence && !isRemoved;

  const KindIcon =
    kind === 'tabular' ? TableChartOutlined
      : kind === 'document' ? DescriptionOutlined
        : InsertDriveFileOutlined;

  const errorMsg = server?.error_message || draft?.uploadError;
  const canOpenDialog = !isUploading;

  const isCompact = variant === 'compact';

  return (
    <>
      <Tooltip
        title={
          isRemoved
            ? `${filename} (removed from this thread)`
            : errorMsg || filename
        }
        placement="top"
      >
        <Box
          onClick={canOpenDialog ? () => setDialogOpen(true) : undefined}
          sx={(t) => ({
            position: 'relative',
            width: isCompact ? 'auto' : 168,
            maxWidth: isCompact ? 240 : 168,
            height: isCompact ? 36 : 64,
            px: isCompact ? 1 : 1.25,
            py: isCompact ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: isCompact ? 0.75 : 1.25,
            borderRadius: `${t.ds.radius.md}px`,
            bgcolor: t.tokens.surface.sunken,
            border: `1px ${isRemoved ? 'dashed' : 'solid'} ${
              isFailed
                ? t.palette.error.main
                : isLowConfidence
                  ? t.palette.warning.main
                  : t.tokens.border.default
            }`,
            opacity: isRemoved ? 0.6 : 1,
            transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
            cursor: canOpenDialog ? 'pointer' : 'default',
            '&:hover': canOpenDialog
              ? {
                  opacity: isRemoved ? 0.85 : 1,
                  bgcolor: t.tokens.surface.raised,
                  borderColor: isFailed
                    ? t.palette.error.main
                    : isLowConfidence
                      ? t.palette.warning.main
                      : t.tokens.border.strong,
                }
              : undefined,
            '&:hover .file-card-remove': { opacity: 1 },
          })}
        >
          {/* Status / kind icon */}
          <Box
            sx={(t) => ({
              width: isCompact ? 20 : 32,
              height: isCompact ? 20 : 32,
              flexShrink: 0,
              borderRadius: `${t.ds.radius.sm}px`,
              bgcolor: isCompact
                ? 'transparent'
                : kind === 'tabular'
                  ? 'success.50'
                  : 'info.50',
              color: kind === 'tabular' ? 'success.main' : 'info.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            {isUploading || isProcessing ? (
              <CircularProgress size={isCompact ? 12 : 14} thickness={5} />
            ) : isFailed ? (
              <ErrorRounded sx={{ fontSize: isCompact ? 14 : 18, color: 'error.main' }} />
            ) : isLowConfidence ? (
              <WarningAmberRounded sx={{ fontSize: isCompact ? 14 : 18, color: 'warning.main' }} />
            ) : isReady && !isCompact ? (
              <CheckCircleRounded sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <KindIcon sx={{ fontSize: isCompact ? 14 : 18 }} />
            )}
          </Box>

          {/* Text block */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: isCompact ? '0.75rem' : '0.8125rem',
                fontWeight: 500,
                lineHeight: 1.25,
                textDecoration: isRemoved ? 'line-through' : 'none',
                textDecorationColor: 'currentColor',
                textDecorationThickness: '1px',
                ...(isCompact
                  ? {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  : {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }),
              }}
            >
              {filename}
            </Typography>
            {!isCompact && (
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontSize: '0.6875rem',
                  color: t.tokens.text.tertiary,
                  mt: 0.25,
                  display: 'block',
                })}
              >
                {isUploading
                  ? 'Uploading…'
                  : isProcessing
                    ? 'Analyzing…'
                    : isFailed
                      ? 'Failed'
                      : isRemoved
                        ? 'Removed'
                        : isLowConfidence
                          ? 'Confirm parse'
                          : ext || 'FILE'}
              </Typography>
            )}
          </Box>

          {/* Remove */}
          {onRemove && (
            <IconButton
              className="file-card-remove"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={(t) => ({
                position: 'absolute',
                top: isCompact ? '50%' : -6,
                right: isCompact ? 4 : -6,
                transform: isCompact ? 'translateY(-50%)' : 'none',
                width: 18,
                height: 18,
                bgcolor: t.tokens.surface.raised,
                border: isCompact ? 'none' : `1px solid ${t.tokens.border.default}`,
                color: t.tokens.text.secondary,
                opacity: isCompact ? 0.5 : 0,
                transition: `opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                '&:hover': {
                  bgcolor: t.tokens.surface.raised,
                  color: t.tokens.text.primary,
                  opacity: 1,
                },
              })}
              aria-label={`Remove ${filename}`}
            >
              <CloseRounded sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Tooltip>

      <AttachmentDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        server={server}
        draft={draft}
        onConfirm={onConfirm}
      />
    </>
  );
}

function guessFileKind(file: File): 'tabular' | 'document' | 'image' {
  const name = file.name.toLowerCase();
  if (file.type.startsWith('image/')) return 'image';
  if (/\.(csv|tsv|xlsx?|parquet)$/.test(name)) return 'tabular';
  return 'document';
}
